// @mui
import { styled, alpha } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar } from '@mui/material';
// hooks
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
import usePermissions from '../../../hooks/usePermissions';
// utils
import cssStyles from '../../../utils/cssStyles';
// config
import { HEADER, NAVBAR } from '../../../config';
// components
import Logo from '../../../components/Logo';
import Iconify from '../../../components/Iconify';
import { IconButtonAnimate } from '../../../components/animate';
//
import Searchbar from './Searchbar';
import AccountPopover from './AccountPopover';
import VenuePopover from './VenuePopover';
import ThemeModeSwitch from './ThemeModeSwitch';
import ProfilePreviewHeaderBar from './ProfilePreviewHeaderBar';
import ConnectionIndicator from './ConnectionIndicator';

// ----------------------------------------------------------------------

type RootStyleProps = {
  isCollapse: boolean;
  isOffset: boolean;
  verticalLayout: boolean;
  isPreviewActive: boolean;
};

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) =>
    prop !== 'isCollapse' &&
    prop !== 'isOffset' &&
    prop !== 'verticalLayout' &&
    prop !== 'isPreviewActive',
})<RootStyleProps>(({ isCollapse, isOffset, verticalLayout, isPreviewActive, theme }) => ({
  ...cssStyles(theme).bgBlur(),
  boxShadow: 'none',
  height: HEADER.MOBILE_HEIGHT,
  zIndex: theme.zIndex.appBar + 1,
  transition: theme.transitions.create(['width', 'height', 'background-color', 'border-color'], {
    duration: theme.transitions.duration.shorter,
  }),
  ...(isPreviewActive && {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.warning.main, 0.24)
        : alpha(theme.palette.warning.light, 0.94),
    backdropFilter: 'blur(8px)',
    borderBottom: `2px solid ${theme.palette.warning.main}`,
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.warning.light
        : theme.palette.warning.darker,
  }),
  [theme.breakpoints.up('lg')]: {
    height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
    ...(isCollapse && {
      width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`,
    }),
    ...(isOffset && {
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
    }),
    ...(verticalLayout && {
      width: '100%',
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
      backgroundColor: isPreviewActive
        ? theme.palette.mode === 'dark'
          ? alpha(theme.palette.warning.main, 0.24)
          : alpha(theme.palette.warning.light, 0.94)
        : theme.palette.background.default,
    }),
  },
}));

// ----------------------------------------------------------------------

type Props = {
  onOpenSidebar: VoidFunction;
  isCollapse?: boolean;
  verticalLayout?: boolean;
};

export default function DashboardHeader({
  onOpenSidebar,
  isCollapse = false,
  verticalLayout = false,
}: Props) {
  const isOffset = useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;
  const { isPreviewActive } = usePermissions();

  const isDesktop = useResponsive('up', 'lg');
  const isMobile = useResponsive('down', 'md');

  const headerForeground = isPreviewActive ? 'inherit' : 'text.primary';

  return (
    <RootStyle
      isCollapse={isCollapse}
      isOffset={isOffset}
      verticalLayout={verticalLayout}
      isPreviewActive={isPreviewActive}
    >
      <Toolbar
        sx={{
          minHeight: '100% !important',
          px: { lg: 5 },
          color: isPreviewActive ? 'inherit' : undefined,
          position: 'relative',
        }}
      >
        <Stack direction="row" alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          {isDesktop && verticalLayout && <Logo sx={{ mr: 2.5 }} />}

          {!isDesktop && (
            <IconButtonAnimate onClick={onOpenSidebar} sx={{ mr: 1, color: headerForeground }}>
              <Iconify icon="eva:menu-2-fill" />
            </IconButtonAnimate>
          )}

          <Searchbar />
        </Stack>

        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: { xs: 'calc(100% - 160px)', sm: '45%', md: '40%' },
            pointerEvents: 'none',
          }}
        >
          <Box sx={{ pointerEvents: 'auto', display: 'flex', justifyContent: 'center' }}>
            <VenuePopover />
          </Box>
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          spacing={{ xs: 0.5, sm: 1.5 }}
          sx={{ flex: 1, justifyContent: 'flex-end', minWidth: 0 }}
        >
          {isPreviewActive ? <ProfilePreviewHeaderBar /> : null}

          {!isMobile && <ConnectionIndicator />}

          <ThemeModeSwitch inPreviewHeader={isPreviewActive} />

          <AccountPopover />
        </Stack>
      </Toolbar>
    </RootStyle>
  );
}
